import { useCallback, useContext } from 'react';
import { isCreatingNotSendingDocument } from './isCreatingNotSendingDocument';
import { useToast } from '../../../hooks/useToast';
import { capitalize } from '../../../blocks/helpers';
import {
	createBundleObject,
	getIsLastDocumentInBundle,
	updateBundleCookieAfterSending,
} from '..';
import { BundleCookie } from './bundleCookieType';
import { getNextDocumentPathToEditInBundle } from './getNextDocumentPathToEditInBundle';
import { useBlockingRouter } from '../../../hooks/useBlockingRouter';
import {
	DocumentTypeEnum,
	suggestDocumentCreateModalCookieDataExists,
	updateSuggestDocumentCreateModalDataAfterSending,
} from '../../../blocks/SuggestDocumentCreateModal';
import { ModalContext, ModalType } from '../../../context/ModalContextProvider';
import { INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID } from '../../editorBlockerIds';

type CreateBundleReturnType = NonNullable<
	ReturnType<typeof createBundleObject>
>;

export const useDocumentEditFollowupActions = ( {
	documentType,
	isResending,
	bundle,
}: {
	documentType: DocumentTypeEnum;
	isResending: boolean;
	// if we have a bundle already, then use that. Otherwise we need some data in case we need to create a bundle
	bundle?: CreateBundleReturnType;
} ) => {
	const { showSuccess } = useToast();
	const { push, query, pathname } = useBlockingRouter();

	const suggestDocumentCreateModalDataExists =
		suggestDocumentCreateModalCookieDataExists();

	const isLastDocumentInBundle = suggestDocumentCreateModalDataExists
		? false
		: getIsLastDocumentInBundle( documentType, bundle?.contactID );

	const goToNextDocumentInBundleOnSend =
		suggestDocumentCreateModalDataExists ||
		( !!bundle && !isLastDocumentInBundle );

	const handleDefaultRoute = useCallback(
		async ( documentId: string ) => {
			if (
				pathname !== `/${ documentType.toLowerCase() }/[id]` ||
				query.tab !== 'view' ||
				query.id !== documentId
			) {
				await push( `/${ documentType.toLowerCase() }/${ documentId }?tab=view`, {
					overrideBlockers: [ INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID ],
				} );
			}
		},
		[
			pathname,
			query,
			push,
			documentType
		]
	);

	const modalContext = useContext( ModalContext );

	const onSend = useCallback(
		async ( {
			documentId,
			contactID,
			// remember the `bundle: boolean` here indicates that this is actually a bundle (cookie) or just values passed in (not from a cookie)
			...bundleData
		}: Omit<
			BundleCookie,
			'proposalBundleStatus' | 'contractBundleStatus' | 'invoiceBundleStatus'
		> & {
			documentId: string;
		} ) => {
			showSuccess(
				isResending
					? `${ capitalize( documentType ) } resent`
					: isCreatingNotSendingDocument( bundle, documentType )
						? `${ capitalize( documentType ) } created`
						: `${ capitalize( documentType ) } sent`
			);
			// Update the bundle cookie for the contact on send
			const updatedBundleCookie = bundle?.contactID
				? updateBundleCookieAfterSending( {
					contactID,
					...( documentType === DocumentTypeEnum.Proposal
						? { proposalSent: true }
						: {} ),
					...( documentType === DocumentTypeEnum.Contract
						? { contractSent: true }
						: {} ),
					...( documentType === DocumentTypeEnum.Invoice
						? { invoiceSent: true }
						: {} ),
					...bundleData,
				  } )
				: undefined;
			// also remove the suggest document create modal data to clean the slate for the next document
			updateSuggestDocumentCreateModalDataAfterSending(
				documentType === DocumentTypeEnum.Invoice
			);
			// determine the next document path to edit in the bundle, if there is a bundle
			const nextDocumentPath =
				getNextDocumentPathToEditInBundle( updatedBundleCookie );
			if ( nextDocumentPath && goToNextDocumentInBundleOnSend ) {
				await push( nextDocumentPath );
			} else if ( !isLastDocumentInBundle ) {
				if ( documentType !== DocumentTypeEnum.Invoice ) {
					modalContext?.addModal( ModalType.SuggestDocumentCreateModal, {
						open: true,
						contact: contactID,
						eventDate: bundleData.eventDate,
						dueDate: bundleData.dueDate,
						title: bundleData.title,
						nextDocumentType:
							documentType === DocumentTypeEnum.Proposal
								? DocumentTypeEnum.Contract
								: DocumentTypeEnum.Invoice,
						previousDocumentType: documentType,
					} );
				}
				await handleDefaultRoute( documentId );
			} else if ( isLastDocumentInBundle ) {
				await handleDefaultRoute( documentId );
			}
		},
		[
			showSuccess,
			isResending,
			documentType,
			bundle,
			goToNextDocumentInBundleOnSend,
			isLastDocumentInBundle,
			push,
			handleDefaultRoute,
			modalContext,
		]
	);

	return {
		onSend,
		isLastDocumentInBundle,
	};
};
